import { useState, useLayoutEffect } from 'react';

/** Fuse-like mobile: rail + drawer below Tailwind `lg` (1024px). */
const NARROW_MAX_PX = 1023;
export const LAYOUT_NARROW_MQ = `(max-width: ${NARROW_MAX_PX}px)`;

/**
 * Mobile-portrait breakpoint — mirrors AWA/FEB pattern from bitacora-040526.
 * Includes dimension fallback for DevTools where orientation media query can lag.
 */
export const MOBILE_PORTRAIT_MQ = '(max-width: 600px) and (orientation: portrait)';

/** Snapshot for early heartbeat — drawer 314 vs rail 64 (Docs/Fuse-like-4-mobile.md). */
export const AIRECORDING_FUSE_METRICS_SESSION_KEY = 'aiRecording:fuseSidebarMetricsSync';

export function matchesNarrowLayoutSync(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return window.matchMedia(LAYOUT_NARROW_MQ).matches;
  } catch {
    return window.innerWidth <= NARROW_MAX_PX;
  }
}

/**
 * Sync portrait check with dimension fallback — same pattern as AWA/FEB
 * (fuseMobileLessonSidebarMetrics.js, bitacora-040526).
 */
export function matchesMobilePortraitSync(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia(MOBILE_PORTRAIT_MQ).matches) return true;
  } catch { /* fallback */ }
  const w = Math.round(window.innerWidth || 0);
  const h = Math.round(window.innerHeight || 0);
  return w > 0 && w <= 600 && h > w;
}

export function useNarrowLayout(): boolean {
  const [narrow, setNarrow] = useState(false);

  useLayoutEffect(() => {
    const mql = window.matchMedia(LAYOUT_NARROW_MQ);
    const update = () => {
      const matches = mql.matches;
      setNarrow(matches);
      document.documentElement.setAttribute('data-narrow-layout', matches ? '1' : '0');
    };
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  return narrow;
}

/** Reactive portrait hook — registers both matchMedia and resize for DevTools reliability. */
export function useMobilePortrait(): boolean {
  const [isMobilePortrait, setIsMobilePortrait] = useState(matchesMobilePortraitSync);

  useLayoutEffect(() => {
    const update = () => setIsMobilePortrait(matchesMobilePortraitSync());
    let mq: MediaQueryList | undefined;
    try {
      mq = window.matchMedia(MOBILE_PORTRAIT_MQ);
      mq.addEventListener('change', update);
    } catch { /* ignore */ }
    window.addEventListener('resize', update);
    update();
    return () => {
      if (mq) try { mq.removeEventListener('change', update); } catch { /* ignore */ }
      window.removeEventListener('resize', update);
    };
  }, []);

  return isMobilePortrait;
}
